import {NavigationGuard, RouteMeta, RouteRecordRaw} from 'vue-router';
import store from '@/store/index';
import i18n, { getLocale } from '@/i18n/index';

// Use dynamic imports to avoid circular dependencies
const Startpage = () => import('./pages/Startpage.vue');
const FAQ = () => import('./pages/FAQ.vue');
const Impressum = () => import('./pages/Impressum.vue');
const Contact = () => import('./pages/Contact/Contact.vue');
const ContactHolder = () => import('./pages/Contact/ContactHolder.vue');
const ContactSuccess = () => import('./pages/Contact/ContactSuccess.vue');
const Appointment = () => import('./pages/Appointment/Appointment.vue');
const AppointmentHolder = () => import('./pages/Appointment/AppointmentHolder.vue');
const AppointmentSelection = () => import('./pages/Appointment/AppointmentSelection.vue');
const AppointmentConfirmation = () => import('./pages/Appointment/AppointmentConfirmation.vue');
const AppointmentSummary = () => import('./pages/Appointment/AppointmentSummary.vue');
const Order = () => import('./pages/Order.vue');
const Tracking = () => import('./pages/Tracking.vue');
const Error = () => import('./pages/Error.vue');
const Accessibility = () => import('./pages/Accessibility.vue');
const PrivacyNotice = () => import('./pages/PrivacyNotice.vue');
const GTC = () => import('./pages/GTC.vue');
const AdditionalServices = () => import('./pages/AdditionalServices.vue');

interface RouteItem {
	pathName: string; // The name of the route in the translation files
	name?: string; // The name of the component
	meta?: RouteMeta;
	component: RouteRecordRaw['component'];
	children?: Array<RouteItem>;
	alias?: string;
	beforeEnter?: NavigationGuard;
}

// Creates a link to the given route in the current language
export function getRouteName(routeKey: string): string {
	if (routeKey === "error") {
		// Don't set a locale for the error page
		return routeKey;
	}

	const locale = getLocale();

	if(routeKey === "appointmentSelection" )
	{
    const hasAppointment = store.getters.getHasAppointment;
    const canMakeAppointment = store.getters.getCanMakeAppointment;

		if ((hasAppointment || (!hasAppointment && !canMakeAppointment))) {
			return `${locale}.appointmentBase`;
		}
	}

	return `${locale}.${routeKey}`;
}

export function getDirectRouteName(routeKey: string): string {
	return `${getLocale()}.${routeKey}`;
}

const allRoutes: Array<RouteItem> = [
	{
		pathName: '',
		name: 'startpage',
		component: Startpage,
		meta: {
			disallowAuth: true
		}
	},
	{
		pathName: 'faq',
		name: 'faq',
		component: FAQ,
	},
	{
		pathName: 'imprint',
		name: 'imprint',
		component: Impressum,
	},
	{
		pathName: 'accessibility',
		name: 'accessibility',
		component: Accessibility,
	},

	{
		pathName: 'contact',
		component: ContactHolder,
		children: [
			{
				pathName: 'contact',
				name: 'contact',
				component: Contact
			},
			{
				pathName: 'contact/thanks',
				name: 'contactSuccess',
				component: ContactSuccess
			}
		]
	},
	{
		pathName: 'orderinformation',
		name: 'order',
		component: Order,
		meta: {
			requiresAuth: true,
		}
	},
	{
		pathName: 'trackandtrace',
		name: 'tracking',
		component: Tracking,
		meta: {
			requiresAuth: true,
		}
	},
	{
		pathName: 'dataprotection',
		name: 'privacy',
		component: PrivacyNotice,
	},
	{
		pathName: 'appointment',
		component: AppointmentHolder,
		meta: {
			requiresAuth: true,
		},
		children: [
			{
				pathName: 'appointment',
				name: 'appointmentBase',
				component: Appointment,
			},
			{
				pathName: 'appointment/selection',
				name: 'appointmentSelection',
				component: AppointmentSelection
			},
			{
				pathName: 'appointment/confirmation',
				name: 'appointmentConfirmation',
				component: AppointmentConfirmation
			},
			{
				pathName: 'appointment/summary',
				name: 'appointmentSummary',
				component: AppointmentSummary
			},
			{
				pathName: 'appointment/gtc',
				name: 'gtc',
				component: GTC
			},
			{
				pathName: 'appointment/additionalservices',
				name: 'additionalServices',
				component: AdditionalServices,
			}
		]
	}
];

function convertChildRouteConfigToRoute(routeChild: RouteItem, languageCode: string): RouteRecordRaw {
	return {
		path: `/${languageCode}/${routeChild.pathName}`,
		component: routeChild.component,
		meta: routeChild.meta,
		beforeEnter: routeChild.beforeEnter,
		name: `${languageCode}.${routeChild.name}`,
    children: [],
	};
}

function convertRouteConfigToRoutes(routeItem: RouteItem): RouteRecordRaw[] {
	// Take each configured config item and convert it into a format that the vue-router can use. One entry will be created for each
	// language that the site is configured to support
	const messages = (i18n.global as any).messages || {};
	const allLanguageNames = i18n.global?.availableLocales || Object.keys(messages);
	const items = allLanguageNames.map((languageCode: string) => {
		const item: RouteRecordRaw = {
			path: `/${languageCode}/${routeItem.pathName}`,
			component: routeItem.component,
			meta: routeItem.meta,
			beforeEnter: routeItem.beforeEnter,
      children: [],
		};
		if (routeItem.name !== undefined) {
			item.name = `${languageCode}.${routeItem.name}`;
		}
		if (routeItem.children !== undefined && routeItem.children.length > 0) {
			item.children = routeItem.children.map(x => convertChildRouteConfigToRoute(x, languageCode));
		}

		return item;
	});
	return items;
}

export function getAllRoutes(): RouteRecordRaw[] {
	const routeConfigs = allRoutes.flatMap(routeConfig => convertRouteConfigToRoutes(routeConfig));
	// Add a catch-all error path (Vue Router 4 syntax)
	routeConfigs.push({
		path: '/:pathMatch(.*)*',
		name: 'error',
		component: Error
	});

	// Add a route to redirect the user to the homepage to the current language (or the default if not set) if they try to access the site root
  const startPage = allRoutes.find(s => s.name === "startpage");
	routeConfigs.unshift({
		path: "/",
		redirect: startPage.pathName + "/" + getLocale()
	});
	return routeConfigs;
}
